import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { LoadingScreen, RequestState } from 'ds';
import { useAppSelector } from 'store/hooks';

import TenantEmployeeRequestPageContent from './TenantEmployeeRequestPageContent';
import { getTenantEmployeeRequest, getTenantEmployeeRequests } from './requests';
import { TenantEmployeeRequest } from './types';

interface Props {}

const TenantEmployeeRequestPage: React.FC<Props> = () => {
  const { id: idStr } = useParams<{ id: string }>();
  const id = parseInt(idStr, 10);
  const tenancyId = useAppSelector(state => state.v2.User.user?.tenancy_id);
  const [listState, setListState] = useState<RequestState>('in_progress');
  const [requests, setRequests] = useState<TenantEmployeeRequest[]>([]);
  const [detailRequest, setDetailRequest] = useState<TenantEmployeeRequest | null>(null);
  const [detailStatus, setDetailStatus] = useState<'idle' | 'loading' | 'ready'>('idle');

  const inList = !Number.isNaN(id) && requests.some((req: TenantEmployeeRequest) => req.id === id);
  const request = requests.find((req: TenantEmployeeRequest) => req.id === id) ?? detailRequest;

  useEffect(() => {
    const fetchRequests = async () => {
      if (!tenancyId) {
        setListState('ready');
        return;
      }

      setListState('in_progress');
      try {
        const { data } = await getTenantEmployeeRequests({ tenancyId });
        setRequests(data);
        setListState('ready');
      } catch (error) {
        console.error('Failed to fetch tenant employee requests:', error);
        setListState('ready');
      }
    };

    fetchRequests();
  }, [tenancyId]);

  useEffect(() => {
    if (listState !== 'ready' || !tenancyId || Number.isNaN(id)) {
      return;
    }

    if (inList) {
      setDetailRequest(null);
      setDetailStatus('idle');
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      setDetailStatus('loading');
      try {
        const { data } = await getTenantEmployeeRequest({ tenancyId, requestId: id });
        if (!cancelled) {
          setDetailRequest(data);
        }
      } catch (error) {
        console.error('Failed to fetch tenant employee request:', error);
        if (!cancelled) {
          setDetailRequest(null);
        }
      } finally {
        if (!cancelled) {
          setDetailStatus('ready');
        }
      }
    };

    void fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [listState, tenancyId, id, inList, requests]);

  const needsDetail = listState === 'ready' && Boolean(tenancyId) && !Number.isNaN(id) && !inList;

  const loading = listState === 'in_progress' || (needsDetail && detailStatus !== 'ready');

  if (loading) {
    return <LoadingScreen />;
  }

  if (!request) {
    return null;
  }

  const handleRequestUpdated = (updatedRequest: TenantEmployeeRequest) => {
    setRequests(prev => prev.map(r => (r.id === updatedRequest.id ? updatedRequest : r)));
    setDetailRequest(prev => (prev !== null && prev.id === updatedRequest.id ? updatedRequest : prev));
  };

  return (
    <TenantEmployeeRequestPageContent
      tenantEmployeeRequest={request}
      onRequestUpdated={handleRequestUpdated}
      key={request.id}
    />
  );
};

export default TenantEmployeeRequestPage;
